// src/pages/About.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { motion } from 'framer-motion';
import 'swiper/css';
import 'swiper/css/autoplay';

// === ALL IMAGES ===
import heroBg from '../assets/Japan/pexels-sunhung94-25172743.jpg';

import slide1 from '../assets/Japan/29244c79cad747a99148b3c6f2ff1409_cut_600x550_508_1749045375.webp';
import slide2 from '../assets/Japan/pexels-agustin-villalba-589020055-17258243.jpg';
import slide3 from '../assets/Japan/images.jpeg';
import slide4 from '../assets/Japan/images (1).jpeg';
import slide5 from '../assets/Japan/IGI_6551.webp';

import tourImage1 from '../assets/Japan/811ddd7d-2a71-4a17-a7c9-b5689d5be120.webp';


import tourImage4 from '../assets/Japan/fushimi-inari-taisha-shrine-2_optimized.jpg';
import tourImage5 from '../assets/Japan/HakonePrivateCharteredDayTour(FromTokyo).jpg';


import aboutImage from '../assets/Japan/IMG_3017.jpg';
import offer1 from '../assets/Japan/image-155-1200x720.png';
import offer2 from '../assets/Japan/Nachi-Japan-Outdoor-Adventures.jpg';
import offer3 from '../assets/Japan/luxury-japan-travel-himeji-castle-sakura-heritage.webp';
import offer4 from '../assets/Japan/Japan+Spa+Wellness.png';


import tourExpert from '../assets/Sonic/27.jpeg';
import owner from '../assets/Sonic New/att.XLunJMj7sPQJgNc4efT9_ZXLER_W4UhhoDTruiRIPnA.JPG';
import villageImage from '../assets/Japan/1-17-77166.jpeg';




// === ANIMATION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } },
};

const About = () => {
    const { t } = useTranslation('about');


    return (
        <div className="bg-[#07111f] text-white">
            {/* ===== HERO SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative min-h-[100svh] overflow-hidden bg-cover bg-center py-20 sm:py-24 md:min-h-[90vh] md:py-24 lg:min-h-[95vh] lg:py-28 xl:py-32"
                style={{
                    backgroundImage: `linear-gradient(0deg, rgba(0,0,0,0.78) 20%, rgba(0,0,0,0.35) 65%, rgba(0,0,0,0.2) 100%), url(${heroBg})`,
                }}
            >
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex h-full flex-col justify-between gap-8 md:gap-10">
                        <div className="max-w-4xl pt-8 sm:pt-10 md:pt-12 lg:pt-14">
                            <div className="inline-block">
                                <h1 className="flex flex-col font-bold leading-[0.95]">
                                    <span className="text-[2.2rem] text-[#7fd3ff] sm:text-[2.8rem] md:text-[3.5rem] lg:text-[5rem] xl:text-[6.25rem]">
                                        {t('hero.discover')}
                                    </span>
                                    <span className="mt-2 text-[2.7rem] text-[#f7d774] sm:text-[3.4rem] md:text-[4.2rem] lg:text-[6.2rem] xl:text-[8.75rem]">
                                        {t('hero.starLankaTours')}
                                    </span>
                                </h1>
                                <div className="mt-4 h-[3px] w-20 rounded-full bg-[#f7d774] sm:w-28 md:w-32 lg:w-40"></div>
                            </div>

                            <p className="mt-5 max-w-xl text-sm leading-7 text-white/85 sm:text-base lg:text-lg">
                                {t('hero.tagline1')}
                            </p>
                        </div>

                        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                            <div className="w-full lg:w-[64%]">
                                <div className="overflow-hidden rounded-[1.25rem] border border-white/15 bg-black/20 shadow-[0_20px_40px_rgba(0,0,0,0.25)] backdrop-blur-sm">
                                    <Swiper
                                        modules={[Autoplay]}
                                        slidesPerView={1.15}
                                        spaceBetween={12}
                                        loop
                                        autoplay={{ delay: 2500, disableOnInteraction: false, pauseOnMouseEnter: true }}
                                        breakpoints={{
                                            640: { slidesPerView: 1.9 },
                                            768: { slidesPerView: 2.2 },
                                            1024: { slidesPerView: 2.8 },
                                            1280: { slidesPerView: 3.2 },
                                        }}
                                        className="h-[220px] sm:h-[250px] md:h-[260px] lg:h-[300px]"
                                    >
                                        <SwiperSlide>
                                            <div className="relative h-full w-full">
                                                <img src={slide1} alt={t('slider.sigiriya')} className="h-full w-full rounded-[1rem] object-cover" />
                                                <h4 className="absolute bottom-3 left-3 max-w-[140px] text-sm font-semibold text-white sm:text-base lg:text-lg">{t('slider.sigiriya')}</h4>
                                            </div>
                                        </SwiperSlide>
                                        <SwiperSlide>
                                            <div className="relative h-full w-full">
                                                <img src={slide2} alt={t('slider.yala')} className="h-full w-full rounded-[1rem] object-cover" />
                                                <h4 className="absolute bottom-3 left-3 max-w-[140px] text-sm font-semibold text-white sm:text-base lg:text-lg">{t('slider.yala')}</h4>
                                            </div>
                                        </SwiperSlide>
                                        <SwiperSlide>
                                            <div className="relative h-full w-full">
                                                <img src={slide3} alt={t('slider.tea')} className="h-full w-full rounded-[1rem] object-cover" />
                                                <h4 className="absolute bottom-3 left-3 max-w-[140px] text-sm font-semibold text-white sm:text-base lg:text-lg">{t('slider.tea')}</h4>
                                            </div>
                                        </SwiperSlide>
                                        <SwiperSlide>
                                            <div className="relative h-full w-full">
                                                <img src={slide4} alt={t('slider.beaches')} className="h-full w-full rounded-[1rem] object-cover" />
                                                <h4 className="absolute bottom-3 left-3 max-w-[140px] text-sm font-semibold text-white sm:text-base lg:text-lg">{t('slider.beaches')}</h4>
                                            </div>
                                        </SwiperSlide>
                                        <SwiperSlide>
                                            <div className="relative h-full w-full">
                                                <img src={slide5} alt={t('slider.temples')} className="h-full w-full rounded-[1rem] object-cover" />
                                                <h4 className="absolute bottom-3 left-3 max-w-[140px] text-sm font-semibold text-white sm:text-base lg:text-lg">{t('slider.temples')}</h4>
                                            </div>
                                        </SwiperSlide>
                                    </Swiper>
                                </div>
                            </div>

                            <div className="w-full max-w-xl lg:w-[32%] lg:max-w-none">
                                <div className="rounded-[1.25rem] border border-[#f7d774]/25 bg-[#07111f]/70 p-4 shadow-[0_15px_35px_rgba(0,0,0,0.25)] backdrop-blur-sm sm:p-5">
                                    <p className="text-sm leading-7 text-white/90 sm:text-base">
                                        {t('hero.tagline2')}
                                    </p>
                                    <div className="mt-4 h-1 w-24 rounded-full bg-[#7fd3ff]"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* ===== ABOUT AAMY TOURS SECTION (Updated - Modern Card + Overlap Style) ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.25 }}
                variants={sectionVariants}
                className="bg-[#07111f] py-16 md:py-20 lg:py-24 overflow-hidden border-t border-white/10"
            >
                <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
                    {/* Header */}
                    <div className="mb-10 max-w-3xl">
                        <p className="text-sm font-semibold uppercase tracking-[0.35em] text-[#f7d774]">
                            {t('about.eyebrow') || 'Discover'}
                        </p>
                        <h2 className="mt-4 font-serif text-3xl font-semibold text-white sm:text-4xl">
                            {t('about.title')}
                        </h2>
                    </div>

                    {/* Main content - grid layout with featured signature + tour highlights */}
                    <div className="relative z-10">
                        {/* Tour Highlights - Responsive grid with overlap/hover effect cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                            {/* Card 1 */}
                            <motion.div
                                whileHover={{ y: -12, scale: 1.04 }}
                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                className="group relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0f1d33] shadow-[0_20px_50px_rgba(0,0,0,0.25)]"
                            >
                                <div className="h-56 w-full overflow-hidden">
                                    <img
                                        src={tourImage1}
                                        alt="Colombo - Classic Sri Lanka"
                                        className="h-full w-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                    />
                                </div>
                                <div className="p-7">
                                    <h3 className="text-2xl font-semibold text-[#7fd3ff]">{t('tours.classic')}</h3>
                                    <p className="mt-3 text-sm font-semibold uppercase tracking-[0.25em] text-[#f7d774]">{t('tours.days4')}</p>
                                    <p className="mt-4 text-base leading-7 text-white/80">
                                        {t('tours.desc1')}
                                    </p>
                                </div>
                            </motion.div>

                            {/* Card 2 */}
                            <motion.div
                                whileHover={{ y: -12, scale: 1.04 }}
                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                className="group relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0f1d33] shadow-[0_20px_50px_rgba(0,0,0,0.25)]"
                            >
                                <div className="h-56 w-full overflow-hidden">
                                    <img
                                        src={tourImage4}
                                        alt="Yala - Wildlife Safari"
                                        className="h-full w-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                    />
                                </div>
                                <div className="p-7">
                                    <h3 className="text-2xl font-semibold text-[#7fd3ff]">{t('tours.safari')}</h3>
                                    <p className="mt-3 text-sm font-semibold uppercase tracking-[0.25em] text-[#f7d774]">{t('tours.days5')}</p>
                                    <p className="mt-4 text-base leading-7 text-white/80">
                                        {t('tours.desc2')}
                                    </p>
                                </div>
                            </motion.div>

                            {/* Card 3 */}
                            <motion.div
                                whileHover={{ y: -12, scale: 1.04 }}
                                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                className="group relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0f1d33] shadow-[0_20px_50px_rgba(0,0,0,0.25)]"
                            >
                                <div className="h-56 w-full overflow-hidden">
                                    <img
                                        src={tourImage5}
                                        alt="Ella / Nuwara Eliya - Hill Country Retreat"
                                        className="h-full w-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                    />
                                </div>
                                <div className="p-7">
                                    <h3 className="text-2xl font-semibold text-[#7fd3ff]">{t('tours.retreat')}</h3>
                                    <p className="mt-3 text-sm font-semibold uppercase tracking-[0.25em] text-[#f7d774]">{t('tours.days6')}</p>
                                    <p className="mt-4 text-base leading-7 text-white/80">
                                        {t('tours.desc3')}
                                    </p>
                                </div>
                            </motion.div>
                        </div>

                    </div>
                </div>
            </motion.section>

            {/* ===== OWNER / CEO SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative bg-[#07111f] border-t border-white/10 py-16 md:py-20 lg:py-28 xl:py-32 overflow-hidden"
            >
                <div className="mx-auto container px-6 sm:px-10 lg:px-16">
                    {/* Mobile layout - visible only on small screens */}
                    <div className="block sm:hidden">
                        <div className="space-y-6 text-center">
                            <div>
                                <p className="text-sm font-semibold uppercase tracking-[0.35em] text-[#f7d774]">
                                    {t('owner.title')}
                                </p>
                                <h2 className="mt-3 font-serif text-3xl font-semibold text-white">
                                    {t('owner.name')}
                                </h2>
                            </div>

                            <div>
                                <p className="text-lg font-medium text-[#7fd3ff]">
                                    {t('owner.role')}
                                </p>
                            </div>

                            <div className="relative group mx-auto max-w-md">
                                <div className="absolute -inset-1 bg-gradient-to-r from-[#f7d774] to-[#7fd3ff] rounded-[24px] opacity-70 blur-xl"></div>
                                <img
                                    src={owner}
                                    alt={t('owner.imageAlt')}
                                    className="relative w-full rounded-[24px] border-2 border-[#f7d774]/50 object-cover h-[420px] shadow-[0_20px_50px_rgba(0,0,0,0.25)]"
                                />
                            </div>

                            <div>
                                <p
                                    className="text-base leading-7 text-white/80"
                                    dangerouslySetInnerHTML={{ __html: t('owner.bio') }}
                                />
                            </div>

                            <div className="pt-2">
                                <div className="inline-flex items-center gap-3 rounded-full border border-[#7fd3ff]/30 bg-white/5 px-6 py-3">
                                    <div className="h-2 w-2 rounded-full bg-[#f7d774] animate-pulse"></div>
                                    <span className="text-sm font-semibold text-[#7fd3ff]">Visionary Leadership</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Desktop layout - hidden on small screens */}
                    <div className="hidden sm:block">
                        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20">
                            {/* Left - Image */}
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                transition={{ type: "spring", stiffness: 300 }}
                                className="w-full lg:w-1/2 order-2 lg:order-1"
                            >
                                <div className="relative group">
                                    <div className="absolute -inset-1 bg-gradient-to-r from-[#f7d774] to-[#7fd3ff] rounded-[30px] opacity-0 group-hover:opacity-100 blur-xl transition duration-500"></div>
                                    <img
                                        src={owner}
                                        alt={t('owner.imageAlt')}
                                        className="relative w-full rounded-[30px] shadow-[0_20px_50px_rgba(0,0,0,0.25)] object-cover h-[420px] md:h-[520px] lg:h-[800px] border-2 border-[#f7d774]/50 group-hover:border-[#7fd3ff] transition duration-500"
                                    />
                                </div>
                            </motion.div>

                            {/* Right - Description */}
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                                className="w-full lg:w-1/2 order-1 lg:order-2"
                            >
                                <div className="space-y-6 lg:space-y-8">
                                    <div>
                                        <p className="text-sm font-semibold uppercase tracking-[0.35em] text-[#f7d774]">
                                            {t('owner.title')}
                                        </p>
                                        <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-white">
                                            {t('owner.name')}
                                        </h2>
                                    </div>

                                    <div>
                                        <p className="text-xl font-medium text-[#7fd3ff]">
                                            {t('owner.role')}
                                        </p>
                                    </div>

                                    <div>
                                        <p
                                            className="text-lg leading-8 text-white/80"
                                            dangerouslySetInnerHTML={{ __html: t('owner.bio') }}
                                        />
                                    </div>

                                    <div className="pt-4">
                                        <div className="inline-flex items-center gap-3 rounded-full border border-[#7fd3ff]/30 bg-white/5 px-6 py-3">
                                            <div className="h-2 w-2 rounded-full bg-[#f7d774] animate-pulse"></div>
                                            <span className="text-sm font-semibold text-[#7fd3ff]">Visionary Leadership</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* ===== WHO WE ARE ===== */}
            <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={sectionVariants} className="bg-[#07111f] border-t border-white/10 py-16 md:py-20 lg:py-24">
                <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
                    <div className="grid gap-6 lg:grid-cols-2">
                        {/* Grid Items */}
                        <div className="rounded-[1.25rem] border border-[#7fd3ff]/20 bg-white/5 p-6">
                            <h3 className="text-xl font-semibold text-[#7fd3ff]">{t('whoWeAre.item1.title')}</h3>
                            <p className="mt-3 text-base leading-7 text-white/80">{t('whoWeAre.item1.text')}</p>
                        </div>
                        <div className="rounded-[1.25rem] border border-[#7fd3ff]/20 bg-white/5 p-6">
                            <h3 className="text-xl font-semibold text-[#7fd3ff]">{t('whoWeAre.item2.title')}</h3>
                            <p className="mt-3 text-base leading-7 text-white/80">{t('whoWeAre.item2.text')}</p>
                        </div>
                        <div className="rounded-[1.25rem] border border-[#7fd3ff]/20 bg-white/5 p-6">
                            <h3 className="text-xl font-semibold text-[#7fd3ff]">{t('whoWeAre.item3.title')}</h3>
                            <p className="mt-3 text-base leading-7 text-white/80">{t('whoWeAre.item3.text')}</p>
                        </div>
                        <div className="rounded-[1.25rem] border border-[#7fd3ff]/20 bg-white/5 p-6">
                            <h3 className="text-xl font-semibold text-[#7fd3ff]">{t('whoWeAre.item4.title')}</h3>
                            <p className="mt-3 text-base leading-7 text-white/80">{t('whoWeAre.item4.text')}</p>
                        </div>
                    </div>

                    <div className="mt-16 flex flex-col lg:flex-row items-start justify-between gap-12 lg:gap-20">
                        <div className="container">

                            <h2 className="mt-4 font-serif text-3xl font-semibold text-white sm:text-4xl">{t('whoWeAre.title')}</h2>
                            <p className="mt-5 text-lg leading-8 text-white/80" dangerouslySetInnerHTML={{ __html: t('whoWeAre.description') }} />
                        </div>
                        <img src={aboutImage} alt={t('whoWeAre.imageAlt')} className="w-full lg:w-[450px] rounded-[1.5rem] object-cover h-[350px] md:h-[500px]" />
                    </div>
                </div>
            </motion.section>

            {/* ===== WHAT WE OFFER ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="bg-[#0b1527] border-t border-white/10 py-16 md:py-20 lg:py-24"
            >
                <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
                    <div className="mb-10 max-w-3xl">

                        <h2 className="mt-4 font-serif text-3xl font-semibold text-white sm:text-4xl">
                            {t('offers.title')}
                        </h2>
                    </div>

                    <div className="grid gap-6 lg:grid-cols-2">
                        <article className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0f1d33] shadow-[0_20px_50px_rgba(0,0,0,0.25)]">
                            <div className="relative h-[260px] md:h-[300px] lg:h-[440px] overflow-hidden">
                                <img
                                    src={offer1}
                                    alt={t('offers.cultural.alt')}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                                />
                            </div>
                            <div className="p-7">
                                <h3 className="text-2xl font-semibold text-[#7fd3ff]">{t('offers.cultural.title')}</h3>
                                <p className="mt-4 text-base leading-7 text-white/80">{t('offers.cultural.text')}</p>
                            </div>
                        </article>

                        <article className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0f1d33] shadow-[0_20px_50px_rgba(0,0,0,0.25)]">
                            <div className="relative h-[260px] md:h-[300px] lg:h-[440px] overflow-hidden">
                                <img
                                    src={offer2}
                                    alt={t('offers.adventure.alt')}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                                />
                            </div>
                            <div className="p-7">
                                <h3 className="text-2xl font-semibold text-[#7fd3ff]">{t('offers.adventure.title')}</h3>
                                <p className="mt-4 text-base leading-7 text-white/80">{t('offers.adventure.text')}</p>
                            </div>
                        </article>

                        <article className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0f1d33] shadow-[0_20px_50px_rgba(0,0,0,0.25)]">
                            <div className="relative h-[260px] md:h-[300px] lg:h-[440px] overflow-hidden">
                                <img
                                    src={offer3}
                                    alt={t('offers.luxury.alt')}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                                />
                            </div>
                            <div className="p-7">
                                <h3 className="text-2xl font-semibold text-[#7fd3ff]">{t('offers.luxury.title')}</h3>
                                <p className="mt-4 text-base leading-7 text-white/80">{t('offers.luxury.text')}</p>
                            </div>
                        </article>

                        <article className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0f1d33] shadow-[0_20px_50px_rgba(0,0,0,0.25)]">
                            <div className="relative h-[260px] md:h-[300px] lg:h-[440px] overflow-hidden">
                                <img
                                    src={offer4}
                                    alt={t('offers.wellness.alt')}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                                />
                            </div>
                            <div className="p-7">
                                <h3 className="text-2xl font-semibold text-[#7fd3ff]">{t('offers.wellness.title')}</h3>
                                <p className="mt-4 text-base leading-7 text-white/80">{t('offers.wellness.text')}</p>
                            </div>
                        </article>
                    </div>
                </div>
            </motion.section>

            {/* ===== TOUR EXPERT ===== */}
            <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={sectionVariants} className="relative bg-[#07111f] border-t border-white/10 py-16 md:py-20 lg:py-28 xl:py-32 overflow-hidden">

                <div className="mx-auto container px-6 sm:px-10 lg:px-16">
                    {/* Mobile layout - visible only on small screens */}
                    <div className="block sm:hidden">
                        <div className="space-y-6 text-center">
                            <div>
                                <h3 className="font-serif text-3xl font-semibold text-[#7fd3ff]">
                                    {t('expert.name')}
                                </h3>
                            </div>

                            <div>
                                <p className="text-sm font-semibold uppercase tracking-[0.35em] text-[#f7d774]">
                                    {t('expert.role')}
                                </p>
                            </div>

                            <div className="relative group mx-auto max-w-md">
                                <div className="absolute -inset-1 bg-gradient-to-r from-[#7fd3ff] to-[#f7d774] rounded-[24px] opacity-70 blur-xl"></div>
                                <img
                                    src={tourExpert}
                                    alt={t('expert.imageAlt')}
                                    className="relative w-full rounded-[24px] border-2 border-[#7fd3ff]/50 object-cover h-[320px] shadow-[0_20px_50px_rgba(0,0,0,0.25)]"
                                />
                            </div>

                            <div>
                                <p
                                    className="text-base leading-7 text-white/80"
                                    dangerouslySetInnerHTML={{ __html: t('expert.bio') }}
                                />
                            </div>

                            <div className="pt-2">
                                <div className="inline-block px-6 py-3 rounded-full border border-[#7fd3ff]/20 bg-white/5 hover:bg-white/10 transition duration-300">
                                    <p className="text-base font-semibold text-[#7fd3ff]">Expert Guide & Travel Consultant</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Desktop layout - hidden on small screens */}
                    <div className="hidden sm:block">
                        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20">
                            {/* Image Section */}
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                transition={{ type: "spring", stiffness: 300 }}
                                className="w-full lg:w-1/2"
                            >
                                <div className="relative group">
                                    <div className="absolute -inset-1 bg-gradient-to-r from-[#7fd3ff] to-[#f7d774] rounded-[30px] opacity-0 group-hover:opacity-100 blur-xl transition duration-500"></div>
                                    <img
                                        src={tourExpert}
                                        alt={t('expert.imageAlt')}
                                        className="relative w-full rounded-[30px] shadow-[0_20px_50px_rgba(0,0,0,0.25)] object-cover h-[400px] md:h-[500px] lg:h-[550px] border-2 border-[#7fd3ff]/50 group-hover:border-[#f7d774] transition duration-500"
                                    />
                                </div>
                            </motion.div>

                            {/* Text Section */}
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                                className="w-full lg:w-1/2"
                            >
                                <div className="space-y-6 lg:space-y-8">
                                    <div>
                                        <h3 className="font-serif text-4xl font-semibold text-[#7fd3ff] sm:text-5xl lg:text-6xl">
                                            {t('expert.name')}
                                        </h3>
                                    </div>

                                    <div>
                                        <p className="text-sm font-semibold uppercase tracking-[0.35em] text-[#f7d774]">
                                            {t('expert.role')}
                                        </p>
                                    </div>

                                    <div>
                                        <p
                                            className="text-lg leading-8 text-white/80"
                                            dangerouslySetInnerHTML={{ __html: t('expert.bio') }}
                                        />
                                    </div>

                                    <div className="pt-4">
                                        <div className="inline-block px-6 py-3 rounded-full border border-[#7fd3ff]/20 bg-white/5 hover:bg-white/10 transition duration-300">
                                            <p className="text-base font-semibold text-[#7fd3ff]">Expert Guide & Travel Consultant</p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* ===== AROUND VILLAGE TOURS ===== */}
            <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={sectionVariants} className="relative bg-[#0b1527] border-t border-white/10 py-16 md:py-20 lg:py-28 overflow-hidden">
                <div className="mx-auto container px-6 sm:px-10 lg:px-16">
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20">
                        {/* Text Section */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="w-full lg:w-1/2"
                        >
                            <div className="space-y-6 lg:space-y-8">
                                <div>

                                    <h2 className="mt-4 font-serif text-3xl font-semibold text-white sm:text-4xl lg:text-5xl">
                                        {t('villageTours.title')}
                                    </h2>
                                </div>

                                <div>
                                    <p className="text-lg leading-8 text-white/80">
                                        {t('villageTours.description')}
                                    </p>
                                </div>

                                <div className="pt-4">
                                    <button
                                        onClick={() => window.open('https://wa.me/819082514053?text=Hello%20Sonic%20Tours%20Japan!%20I%20am%20interested%20in%20booking%20a%20Japan%20tour.%20Please%20provide%20more%20details.', '_blank')}
                                        className="rounded-full bg-gradient-to-r from-[#7fd3ff] to-[#f7d774] px-8 py-3 font-bold text-white hover:opacity-90 transition-opacity duration-300"
                                    >
                                        {t('villageTours.bookButton')}
                                    </button>
                                </div>
                            </div>
                        </motion.div>

                        {/* Image Section */}
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            transition={{ type: "spring", stiffness: 300 }}
                            className="w-full lg:w-1/2"
                        >
                            <div className="relative group">
                                <div className="absolute -inset-1 bg-gradient-to-r from-[#f7d774] to-[#7fd3ff] rounded-[30px] opacity-0 group-hover:opacity-100 blur-xl transition duration-500"></div>
                                <img
                                    src={villageImage}
                                    alt={t('villageTours.imageAlt')}
                                    className="relative w-full rounded-[30px] shadow-[0_20px_50px_rgba(0,0,0,0.25)] object-cover h-[400px] md:h-[500px] lg:h-[550px] border-2 border-[#f7d774]/50 group-hover:border-[#7fd3ff] transition duration-500"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </motion.section>




        </div >
    );
};

export default About;